import React, { useEffect, useState } from 'react';
import './EarningsWeekHeader.css';

interface EarningsWeekHeaderProps {
  weekDays: Date[];
  isToday: (d: Date) => boolean;
  onPrev: () => void;
  onNext: () => void;
  customDayLabels?: string[];
  mobile?: boolean;
  selectedDayIndex?: number;
  setSelectedDayIndex?: (i: number) => void;
}

// Custom viewport hook for mobile detection
function useIsMobile(breakpoint: number = 600) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const EarningsWeekHeader: React.FC<EarningsWeekHeaderProps> = ({ weekDays, isToday, onPrev, onNext, customDayLabels, mobile, selectedDayIndex }) => {
  const _isMobile = useIsMobile();
  const isMobile = mobile !== undefined ? mobile : _isMobile;
  
  const format = (date: Date, index: number) => {
    if (customDayLabels && customDayLabels[index]) {
      return {
        name: customDayLabels[index].split(' ')[0],
        num: customDayLabels[index].split(' ')[1] || date.getDate().toString(),
      };
    }
    return {
      name: date.toLocaleDateString('en-US', { weekday: 'short' }),
      num: date.getDate().toString(),
    };
  };

  if (isMobile && (selectedDayIndex === undefined || selectedDayIndex < 0 || selectedDayIndex > 4)) {
    // fallback to 0 if out of bounds, shouldn't happen
    selectedDayIndex = 0;
  }
  const currentDay = isMobile && typeof selectedDayIndex === 'number' ? weekDays[selectedDayIndex] : undefined;

  return (
    <div className="week-header">
      <button className="week-header-btn" onClick={onPrev} aria-label={isMobile ? 'Previous day' : 'Previous week'}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
        </svg>
      </button>
      <div className="week-header-days">
        {!isMobile ? (
          weekDays.map((d, i) => {
            const { name, num } = format(d, i);
            const today = isToday(d);
            return (
              <div key={i} className="week-header-day">
                <span className="week-day-name">{name}</span>
                <span className={`week-day-number ${today ? 'today' : ''}`}>{num}</span>
              </div>
            );
          })
        ) : (
          (() => {
            if (!currentDay) return null;
            const index = selectedDayIndex || 0;
            const { name, num } = format(currentDay, index);
            const today = isToday(currentDay);
            return (
              <div key={index} className="week-header-day current-mobile-day">
                <span className="week-day-name">{name}</span>
                <span className={`week-day-number ${today ? 'today' : ''}`}>{num}</span>
              </div>
            );
          })()
        )}
      </div>
      <button className="week-header-btn" onClick={onNext} aria-label={isMobile ? 'Next day' : 'Next week'}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/>
        </svg>
      </button>
    </div>
  );
};

export default EarningsWeekHeader;


